import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './UploadZone.css';

/**
 * UploadZone - Streamlined document input with combined text/file upload
 *
 * Props:
 * - file: File | null - Currently selected file
 * - onFileChange: (e) => void - Handler for file input change
 * - onFileRemove: () => void - Handler to remove selected file
 * - textInput: string - Text area content
 * - onTextChange: (text) => void - Handler for text input change
 * - onTextSubmit: () => void - Handler for text submission
 * - textUploaded: boolean - Whether text has been converted to file
 * - disabled: boolean - Whether the upload zone is disabled
 */
export default function UploadZone({
  file,
  onFileChange,
  onFileRemove,
  textInput,
  onTextChange,
  onTextSubmit,
  textUploaded,
  disabled = false,
}) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (!disabled && dragCounterRef.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    if (disabled) return;

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const fileExtension = droppedFile.name.split('.').pop().toLowerCase();
      if (['pdf', 'docx', 'doc', 'txt'].includes(fileExtension)) {
        setError(null);
        const event = { target: { files: [droppedFile] } };
        onFileChange(event);
      } else {
        setError(t('uploadZone.fileError'));
      }
    }
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (!['pdf', 'docx', 'doc', 'txt'].includes(fileExtension)) {
        setError(t('uploadZone.fileError'));
        e.target.value = '';
        return;
      }
      setError(null);
    }
    onFileChange(e);
  };

  const handleUploadClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const truncateFilename = (filename) => {
    if (filename.length <= 40) return filename;
    const extension = filename.split('.').pop();
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const truncated = nameWithoutExt.substring(0, 35 - extension.length);
    return `${truncated}...${extension}`;
  };

  // Show file indicator if a file is selected
  if (file) {
    return (
      <div className="upload-zone-compact">
        <div className="file-selected-indicator">
          <svg className="file-check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
            <polyline points="13 2 13 9 20 9" />
            <polyline points="9 15 11 17 15 13" />
          </svg>
          <span className="file-selected-name">{truncateFilename(file.name)}</span>
          <div className="file-action-buttons">
            <button
              type="button"
              className="file-change-button"
              onClick={handleUploadClick}
              disabled={disabled}
            >
              {t('common.change')}
            </button>
            {onFileRemove && (
              <button
                type="button"
                className="file-remove-button"
                onClick={onFileRemove}
                disabled={disabled}
              >
                {t('common.remove')}
              </button>
            )}
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.doc,.txt"
          onChange={handleFileInputChange}
          disabled={disabled}
          className="file-input-hidden"
          aria-hidden="true"
        />
      </div>
    );
  }

  // Show text uploaded indicator
  if (textUploaded) {
    return (
      <div className="upload-zone-compact">
        <div className="file-selected-indicator text-ready">
          <svg className="file-check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
          <span className="file-selected-name">{t('uploadZone.textReadyForAnalysis')}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`upload-zone-compact ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.doc,.txt"
        onChange={handleFileInputChange}
        disabled={disabled}
        className="file-input-hidden"
        aria-hidden="true"
      />

      {/* Text area with integrated actions */}
      <div className="input-container">
        <textarea
          id="document-text"
          className="document-textarea"
          value={textInput}
          onChange={(e) => onTextChange(e.target.value)}
          disabled={disabled}
          placeholder={t('uploadZone.placeholder')}
          rows={4}
        />

        {/* Action bar below textarea */}
        <div className="input-actions">
          <button
            type="button"
            className="upload-file-button"
            onClick={handleUploadClick}
            disabled={disabled}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            {t('uploadZone.uploadFile')}
          </button>
          <span className="file-formats">{t('uploadZone.fileFormats')}</span>
          {textInput && (
            <button
              type="button"
              className="use-text-button"
              onClick={onTextSubmit}
              disabled={disabled}
            >
              {t('uploadZone.useThisText')}
            </button>
          )}
        </div>
      </div>

      {isDragging && (
        <div className="drop-overlay">
          <span>{t('uploadZone.dropFileHere')}</span>
        </div>
      )}

      {error && (
        <p role="alert" aria-live="assertive" className="upload-error">
          {error}
        </p>
      )}
    </div>
  );
}
